import { Box, Theme } from '@mui/joy';

import { BellNotificationSeverity, BellNotificationType } from 'notifications/enums';
import {
  getNotificationSeverityColor,
  getNotificationTypeIcon,
} from 'notifications/utils/notification';
import { Color, ModeType } from 'theme/enums';

interface IProps {
  type: BellNotificationType;
  severity: BellNotificationSeverity;
  isUnread?: boolean;
}

const ICON_BOX_SIZE = 36;
const ICON_SIZE = 20;
const UNREAD_DOT_SIZE = 10;

// Numbered steps, not soft*: the light warning softBg is the app's saturated yellow, and the dark
// soft tints (step 100) all but vanish on the drawer. The dark scale is inverted (theme.tsx).
const getCircleSx = (color: Color) => (theme: Theme) => ({
  backgroundColor: `${color}.100`,
  color: `${color}.500`,
  [theme.getColorSchemeSelector(ModeType.DARK)]: {
    backgroundColor: `${color}.300`,
    color: `${color}.800`,
  },
});

export const NotificationTypeIcon = ({ type, severity, isUnread = false }: IProps) => {
  const color = getNotificationSeverityColor(severity);
  const Icon = getNotificationTypeIcon(type, severity);

  return (
    <Box
      aria-hidden
      data-testid={`notificationTypeIcon-${severity}`}
      data-type={type}
      sx={[
        {
          position: 'relative',
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          width: ICON_BOX_SIZE,
          height: ICON_BOX_SIZE,
          borderRadius: '50%',
        },
        getCircleSx(color),
      ]}
    >
      <Icon size={ICON_SIZE} />

      {isUnread && (
        <Box
          data-testid="notificationUnreadDot"
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: UNREAD_DOT_SIZE,
            height: UNREAD_DOT_SIZE,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'background.surface',
            backgroundColor: 'primary.solidBg',
          }}
        />
      )}
    </Box>
  );
};
